import ReactGA from 'react-ga4';
import { getUTMParams, type UTMParams } from './utm';
import { StatsigClient } from '@statsig/js-client';
import { StatsigSessionReplayPlugin } from '@statsig/session-replay';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';

// Initialize Statsig (call this at app entrypoint, e.g., in main.tsx)
export let statsigClient: StatsigClient | null = null;

export const initStatsig = async (userID: string, customUserObj: Record<string, any> = {}) => {
  try {
    const statsigClientKey = import.meta.env.VITE_STATSIG_CLIENT_KEY;
    if (!statsigClientKey) {
      throw new Error('Statsig client key is missing: set VITE_STATSIG_CLIENT_KEY in your environment');
    }
    statsigClient = new StatsigClient(
      statsigClientKey,
      { userID, ...customUserObj },
      {
        plugins: [
          new StatsigSessionReplayPlugin(),
          new StatsigAutoCapturePlugin(),
        ],
      }
    );
    await statsigClient.initializeAsync();
    console.log('StatsigClient initialized with Session Replay and Auto Capture plugins');
  } catch (error) {
    console.error('Failed to initialize Statsig:', error);
  }
};

export const initGA = () => {
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (!measurementId) {
    console.warn('GA4 Measurement ID not found. Analytics will not be initialized.');
    return;
  }

  try {
    ReactGA.initialize(measurementId);
    console.log('GA4 initialized successfully');
  } catch (error) {
    console.error('Failed to initialize GA4:', error);
  }
};

export const logPageView = () => {
  if (!import.meta.env.VITE_GA_MEASUREMENT_ID) return;
  
  try {
    const utmParams = getUTMParams();
    ReactGA.send({ 
      hitType: 'pageview',
      page: window.location.pathname,
      ...createGAParamsFromUTM(utmParams)
    });
  } catch (error) {
    console.error('Failed to log page view:', error);
  }
};

export const logEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number,
  statsigEventName?: string,
  statsigValue?: string | number,
  statsigMetadata?: Record<string, any>
) => {
  // GA4 logging
  if (import.meta.env.VITE_GA_MEASUREMENT_ID) {
    try {
      const utmParams = getUTMParams();
      ReactGA.event({
        category,
        action,
        label,
        value,
        ...createGAParamsFromUTM(utmParams)
      });
    } catch (error) {
      console.error('Failed to log GA4 event:', error);
    }
  }

  // Statsig logging
  if (statsigEventName && statsigClient) {
    try {
      statsigClient.logEvent(statsigEventName, statsigValue, statsigMetadata);
    } catch (error) {
      console.error('Failed to log Statsig event:', error);
    }
  }
};


export const logEmailSubmission = (success: boolean) => {
  logEvent(
    'Email Form',
    success ? 'Submission Success' : 'Submission Failed',
    'Landing Page'
  );
};

const createGAParamsFromUTM = (utmParams: UTMParams) => {
  const params: Record<string, string> = {};
  
  if (utmParams.source) params.source = utmParams.source;
  if (utmParams.medium) params.medium = utmParams.medium;
  if (utmParams.campaign) params.campaign_name = utmParams.campaign;
  if (utmParams.term) params.campaign_term = utmParams.term;
  if (utmParams.content) params.campaign_content = utmParams.content;

  return params;
};
